import { Platform } from 'react-native';

import { ENV_CONFIG } from '../../../config/environment';
import { supabase } from '../../../lib/supabase';
import {
  getSubscriptionPlans,
  getSubscriptionPlan
} from '../config/subscriptionPlans';
import { SubscriptionPlan, TIER_FEATURES, normalizeSubscriptionTier } from '../types/premiumFeatures';

import { appleStoreBillingHandler } from './appleStoreBillingHandler';
import { entitlementService, UserSubscription } from './entitlementService';
import { googlePlayBillingHandler } from './googlePlayBillingHandler';
import { platformBillingService, PurchaseResult } from './platformBillingService';

export interface CreateSubscriptionRequest {
  userId: string;
  planId: string;
  billingInterval: 'monthly' | 'annual';
  paymentProvider: 'stripe' | 'apple' | 'google';
  paymentProviderId: string;
  customerEmail?: string;
  // Platform billing specific fields
  platform?: 'ios' | 'android' | 'web';
  purchaseToken?: string; // Google Play purchase token
  originalTransactionId?: string; // Apple original transaction ID
  environment?: 'sandbox' | 'production';
}

export interface SubscriptionUsage {
  subscriptionId: string;
  usageType: 'quiz_created' | 'member_added' | 'event_hosted' | 'storage_used' | 'api_call';
  currentUsage: number;
  limit: number;
  isOverLimit: boolean;
}

type SubscriptionMetadata = Record<string, unknown>;

interface SubscriptionStatusUpdate {
  status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete' | 'unpaid';
  updated_at: string;
  canceled_at?: string | null;
  metadata?: SubscriptionMetadata;
}

interface SubscriptionPlanChangeUpdate {
  subscription_type: string;
  updated_at: string;
  metadata: SubscriptionMetadata;
  billing_interval?: 'monthly' | 'annual';
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

export class SubscriptionService {
  /**
   * Get available subscription plans
   */
  getAvailablePlans(): SubscriptionPlan[] {
    // If subscription is disabled, return empty array
    if (!ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED) {
      return [];
    }

    return getSubscriptionPlans();
  }

  /**
   * Get subscription plans for preview mode
   */
  getPreviewPlans(): SubscriptionPlan[] {
    // Always return plans for preview, even when disabled
    return getSubscriptionPlans();
  }

  /**
   * Get personal subscription plans (non-church)
   */
  getPersonalPlans(): SubscriptionPlan[] {
    return this.getAvailablePlans();
  }

  /**
   * Get group subscription plans
   */
  getGroupPlans(): SubscriptionPlan[] {
    return [];
  }

  /**
   * Get user's current subscription
   */
  async getUserSubscription(userId: string): Promise<UserSubscription | null> {
    try {
      return await entitlementService.getUserSubscription(userId);
    } catch (error) {
      console.error('Failed to get user subscription:', error);
      return null;
    }
  }

  /**
   * Create a new subscription
   */
  async createSubscription(request: CreateSubscriptionRequest): Promise<UserSubscription> {
    try {
      // If subscription is disabled, simulate success
      if (!ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED) {
        console.log('Subscription disabled, simulating subscription creation');
        return this.simulateSubscription(request);
      }

      // Get the subscription plan
      const plan = getSubscriptionPlan(request.planId);
      if (!plan) {
        throw new Error(`Subscription plan ${request.planId} not found`);
      }

      // Calculate subscription period
      const now = new Date();
      const periodEnd = new Date(now);
      if (request.billingInterval === 'monthly') {
        periodEnd.setMonth(periodEnd.getMonth() + 1);
      } else {
        periodEnd.setFullYear(periodEnd.getFullYear() + 1);
      }

      // Prepare subscription metadata based on payment provider
      const metadata: Record<string, unknown> = {
        plan_id: request.planId,
        customer_email: request.customerEmail,
        created_via: 'subscription_service',
        created_at: now.toISOString()
      };

      // Add platform-specific metadata
      if (request.paymentProvider === 'stripe') {
        metadata.stripe_status = 'incomplete';
        metadata.needs_payment_completion = true;
      } else if (request.paymentProvider === 'google') {
        metadata.google_play_purchase_token = request.purchaseToken;
        metadata.platform = 'android';
        metadata.environment = request.environment || 'production';
      } else if (request.paymentProvider === 'apple') {
        metadata.apple_original_transaction_id = request.originalTransactionId;
        metadata.platform = 'ios';
        metadata.environment = request.environment || 'production';
      }

      // Create subscription record
      const { data: subscription, error } = await supabase
        .from('user_subscriptions')
        .insert({
          user_id: request.userId,
          subscription_type: plan.tier,
          status: request.paymentProvider === 'stripe' ? 'incomplete' : 'active',
          current_period_start: now.toISOString(),
          current_period_end: periodEnd.toISOString(),
          cancel_at_period_end: false,
          payment_provider: request.paymentProvider,
          payment_provider_id: request.paymentProviderId,
          billing_interval: request.billingInterval,
          price_usd: request.billingInterval === 'monthly' ? plan.price.monthly : plan.price.annual,
          currency: 'USD',
          platform: request.platform || 'web',
          purchase_token: request.purchaseToken,
          original_transaction_id: request.originalTransactionId,
          environment: request.environment,
          metadata
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Grant subscription features
      const features = TIER_FEATURES[normalizeSubscriptionTier(plan.tier)];
      const featureFlags = features.reduce((acc, feature) => {
        acc[feature] = true;
        return acc;
      }, {} as Record<string, boolean>);

      // Update subscription with proper features
      await supabase
        .from('user_subscriptions')
        .update({
          features: featureFlags,
          updated_at: new Date().toISOString()
        })
        .eq('id', subscription.id);

      // Return subscription object
      return {
        id: subscription.id,
        userId: request.userId,
        subscriptionType: plan.tier,
        status: subscription.status,
        currentPeriodStart: new Date(subscription.current_period_start),
        currentPeriodEnd: new Date(subscription.current_period_end),
        cancelAtPeriodEnd: false,
        paymentProvider: request.paymentProvider,
        paymentProviderId: request.paymentProviderId,
      };
    } catch (error) {
      console.error('Failed to create subscription:', error);
      throw error;
    }
  }

  /**
   * Update subscription status
   */
  async updateSubscriptionStatus(
    subscriptionId: string, 
    status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete' | 'unpaid',
    metadata?: SubscriptionMetadata
  ): Promise<void> {
    try {
      const updateData: SubscriptionStatusUpdate = {
        status,
        updated_at: new Date().toISOString()
      };

      // Handle status-specific updates
      if (status === 'canceled') {
        updateData.canceled_at = new Date().toISOString();
      }

      if (metadata) {
        updateData.metadata = metadata;
      }

      const { error } = await supabase
        .from('user_subscriptions')
        .update(updateData)
        .eq('id', subscriptionId);

      if (error) {
        throw error;
      }

      console.log(`Updated subscription ${subscriptionId} status to ${status}`);
    } catch (error) {
      console.error('Failed to update subscription status:', error);
      throw error;
    }
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(userId: string, cancelAtPeriodEnd: boolean = true): Promise<boolean> {
    try {
      // Get active subscription
      const subscription = await this.getUserSubscription(userId);
      if (!subscription || subscription.status !== 'active') {
        throw new Error('No active subscription found');
      }

      // Update subscription
      const { error } = await supabase
        .from('user_subscriptions')
        .update({
          cancel_at_period_end: cancelAtPeriodEnd,
          canceled_at: cancelAtPeriodEnd ? null : new Date().toISOString(),
          status: cancelAtPeriodEnd ? 'active' : 'canceled',
          updated_at: new Date().toISOString(),
          metadata: {
            ...subscription,
            cancellation_reason: 'user_requested',
            canceled_via: 'subscription_service'
          }
        })
        .eq('id', subscription.id);

      if (error) {
        throw error;
      }

      // If immediate cancellation, revoke features
      if (!cancelAtPeriodEnd) {
        await entitlementService.syncSubscriptionEntitlements(userId);
      }

      console.log(`Subscription ${subscription.id} ${cancelAtPeriodEnd ? 'scheduled for cancellation' : 'canceled immediately'}`);
      return true;
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      return false;
    }
  }

  /**
   * Reactivate canceled subscription
   */
  async reactivateSubscription(userId: string): Promise<boolean> {
    try {
      // Get canceled subscription
      const { data: subscription, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('cancel_at_period_end', true)
        .eq('status', 'active')
        .single();

      if (error || !subscription) {
        throw new Error('No subscription scheduled for cancellation found');
      }

      // Reactivate subscription
      const { error: updateError } = await supabase
        .from('user_subscriptions')
        .update({
          cancel_at_period_end: false,
          canceled_at: null,
          updated_at: new Date().toISOString(),
          metadata: {
            ...subscription.metadata,
            reactivated_at: new Date().toISOString(),
            reactivated_via: 'subscription_service'
          }
        })
        .eq('id', subscription.id);

      if (updateError) {
        throw updateError;
      }

      console.log(`Reactivated subscription ${subscription.id}`);
      return true;
    } catch (error) {
      console.error('Failed to reactivate subscription:', error);
      return false;
    }
  }

  /**
   * Change subscription plan
   */
  async changeSubscriptionPlan(
    userId: string,
    newPlanId: string,
    prorationBehavior: 'immediate' | 'next_period' = 'immediate',
    newBillingInterval?: 'monthly' | 'annual'
  ): Promise<UserSubscription> {
    try {
      // Get current subscription
      const currentSubscription = await this.getUserSubscription(userId);
      if (!currentSubscription || currentSubscription.status !== 'active') {
        throw new Error('No active subscription found');
      }

      // Get new plan
      const newPlan = getSubscriptionPlan(newPlanId);
      if (!newPlan) {
        throw new Error(`Subscription plan ${newPlanId} not found`);
      }

      // Update subscription with billing interval if provided
      const updateData: SubscriptionPlanChangeUpdate = {
        subscription_type: newPlan.tier,
        updated_at: new Date().toISOString(),
        metadata: {
          ...currentSubscription.metadata,
          previous_plan: currentSubscription.subscriptionType,
          plan_changed_at: new Date().toISOString(),
          proration_behavior: prorationBehavior
        }
      };

      // Update billing interval if provided
      if (newBillingInterval) {
        updateData.billing_interval = newBillingInterval;
        updateData.metadata.previous_billing_interval = currentSubscription.billingInterval || 'monthly';
        updateData.metadata.billing_changed_at = new Date().toISOString();

        console.log('🔄 [SubscriptionService] Updating billing interval:', {
          from: currentSubscription.billingInterval || 'monthly',
          to: newBillingInterval
        });
      }

      const { error } = await supabase
        .from('user_subscriptions')
        .update(updateData)
        .eq('id', currentSubscription.id);

      if (error) {
        throw error;
      }

      // Update feature entitlements
      await entitlementService.grantSubscriptionFeatures(userId, newPlan.tier);

      // Return updated subscription
      return {
        ...currentSubscription,
        subscriptionType: newPlan.tier
      };
    } catch (error) {
      console.error('Failed to change subscription plan:', error);
      throw error;
    }
  }

  /**
   * Get subscription usage for limits checking
   */
  async getSubscriptionUsage(userId: string): Promise<SubscriptionUsage[]> {
    try {
      const subscription = await this.getUserSubscription(userId);
      if (!subscription) {
        return [];
      }

      const plan = getSubscriptionPlan(subscription.subscriptionType);
      if (!plan) {
        return [];
      }

      // Get current usage from database
      const { data: usage, error } = await supabase
        .from('subscription_usage')
        .select('*')
        .eq('subscription_id', subscription.id)
        .eq('usage_date', new Date().toISOString().split('T')[0]); // Today's date

      if (error) {
        throw error;
      }

      // Map usage to limits
      const usageMap = new Map(usage?.map(u => [u.usage_type, u.usage_count]) || []);
      
      const result: SubscriptionUsage[] = [];

      // Check each limit
      if (plan.limits.customQuizzes !== undefined && plan.limits.customQuizzes > 0) {
        const current = usageMap.get('quiz_created') || 0;
        result.push({
          subscriptionId: subscription.id,
          usageType: 'quiz_created',
          currentUsage: current,
          limit: plan.limits.customQuizzes,
          isOverLimit: current >= plan.limits.customQuizzes
        });
      }

      if (plan.limits.members !== undefined && plan.limits.members > 0) {
        const current = usageMap.get('member_added') || 0;
        result.push({
          subscriptionId: subscription.id,
          usageType: 'member_added',
          currentUsage: current,
          limit: plan.limits.members,
          isOverLimit: current >= plan.limits.members
        });
      }

      if (plan.limits.liveEvents !== undefined && plan.limits.liveEvents > 0) {
        const current = usageMap.get('event_hosted') || 0;
        result.push({
          subscriptionId: subscription.id,
          usageType: 'event_hosted',
          currentUsage: current,
          limit: plan.limits.liveEvents,
          isOverLimit: current >= plan.limits.liveEvents
        });
      }

      return result;
    } catch (error) {
      console.error('Failed to get subscription usage:', error);
      return [];
    }
  }

  /**
   * Record subscription usage
   */
  async recordUsage(
    userId: string, 
    usageType: 'quiz_created' | 'member_added' | 'event_hosted' | 'storage_used' | 'api_call',
    count: number = 1
  ): Promise<boolean> {
    void userId;
    void usageType;
    void count;
    return false;
  }

  /**
   * Check if user can perform action based on subscription limits
   */
  async canPerformAction(
    userId: string, 
    action: 'create_quiz' | 'add_member' | 'host_event' | 'use_storage'
  ): Promise<boolean> {
    try {
      // If monetization is disabled, allow all actions
      if (!ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED) {
        return true;
      }

      const usage = await this.getSubscriptionUsage(userId);
      
      const actionMap: Record<string, 'quiz_created' | 'member_added' | 'event_hosted' | 'storage_used'> = {
        'create_quiz': 'quiz_created',
        'add_member': 'member_added',
        'host_event': 'event_hosted',
        'use_storage': 'storage_used'
      };

      const usageType = actionMap[action];
      if (!usageType) {
        return true; // Unknown action, allow by default
      }

      const relevantUsage = usage.find(u => u.usageType === usageType);
      return !relevantUsage || !relevantUsage.isOverLimit;
    } catch (error) {
      console.error('Failed to check action permission:', error);
      return true; // Default to allowing action on error
    }
  }

  /**
   * Get subscription analytics for user
   */
  async getSubscriptionAnalytics(userId: string): Promise<{
    subscriptionAge: number; // days
    totalSpent: number;
    featuresUsed: string[];
    usageStats: SubscriptionUsage[];
  }> {
    try {
      const subscription = await this.getUserSubscription(userId);
      if (!subscription) {
        return {
          subscriptionAge: 0,
          totalSpent: 0,
          featuresUsed: [],
          usageStats: []
        };
      }

      // Calculate subscription age
      const subscriptionAge = Math.floor(
        (Date.now() - subscription.currentPeriodStart.getTime()) / (1000 * 60 * 60 * 24)
      );

      // Get total spent (simplified - would need payment history in real implementation)
      const plan = getSubscriptionPlan(subscription.subscriptionType);
      const monthsActive = Math.max(1, Math.floor(subscriptionAge / 30));
      const totalSpent = plan ? plan.price.monthly * monthsActive : 0;

      // Get features used
      const featuresUsed = await entitlementService.getUserFeatures(userId);

      // Get usage stats
      const usageStats = await this.getSubscriptionUsage(userId);

      return {
        subscriptionAge,
        totalSpent,
        featuresUsed: featuresUsed.map(f => f.toString()),
        usageStats
      };
    } catch (error) {
      console.error('Failed to get subscription analytics:', error);
      return {
        subscriptionAge: 0,
        totalSpent: 0,
        featuresUsed: [],
        usageStats: []
      };
    }
  }

  /**
   * Simulate subscription for preview mode
   */
  private simulateSubscription(request: CreateSubscriptionRequest): UserSubscription {
    const plan = getSubscriptionPlan(request.planId);
    if (!plan) {
      throw new Error(`Subscription plan ${request.planId} not found`);
    }

    const now = new Date();
    const periodEnd = new Date(now);
    if (request.billingInterval === 'monthly') {
      periodEnd.setMonth(periodEnd.getMonth() + 1);
    } else {
      periodEnd.setFullYear(periodEnd.getFullYear() + 1);
    }

    return {
      id: 'simulated-' + Date.now(),
      userId: request.userId,
      subscriptionType: plan.tier,
      status: 'active',
      currentPeriodStart: now,
      currentPeriodEnd: periodEnd,
      cancelAtPeriodEnd: false,
      paymentProvider: request.paymentProvider,
      paymentProviderId: 'simulated-' + request.paymentProviderId,
    };
  }

  /**
   * Check if user can access Live Events (Group Plans only)
   */
  async canAccessLiveEvents(userId: string): Promise<boolean> {
    console.warn('Live events are no longer supported in the consumer app', { userId });
    return false;
  }

  /**
   * Get maximum participants allowed for user's subscription (Group Plans only)
   */
  async getMaxParticipants(userId: string): Promise<number> {
    throw new Error(`Live events are no longer supported in the consumer app for user ${userId}`);
  }

  /**
   * Get available participant options based on subscription (Group Plans only)
   */
  async getParticipantOptions(userId: string): Promise<number[]> {
    throw new Error(`Live events are no longer supported in the consumer app for user ${userId}`);
  }

  /**
   * Purchase subscription via platform billing (iOS/Android)
   * Handles the entire flow: product purchase → verification → subscription creation
   */
  async purchasePlatformSubscription(
    userId: string,
    productId: string
  ): Promise<{ success: boolean; subscription?: UserSubscription; error?: string }> {
    try {
      // Initialize platform billing
      const isAvailable = await platformBillingService.isPlatformBillingAvailable();
      if (!isAvailable) {
        return {
          success: false,
          error: 'Platform billing is not available on this device'
        };
      }

      // Purchase product
      const purchaseResult: PurchaseResult = await platformBillingService.purchaseProduct(productId);

      if (!purchaseResult.success) {
        return {
          success: false,
          error: purchaseResult.error || 'Purchase failed'
        };
      }

      // Create subscription in database
      const subscriptionRequest: CreateSubscriptionRequest = {
        userId,
        planId: purchaseResult.planId,
        billingInterval: purchaseResult.billingInterval,
        paymentProvider: purchaseResult.platform === 'ios' ? 'apple' : 'google',
        paymentProviderId: purchaseResult.transactionId || purchaseResult.purchaseToken || '',
        platform: purchaseResult.platform,
        purchaseToken: purchaseResult.purchaseToken,
        originalTransactionId: purchaseResult.transactionId,
        environment: __DEV__ ? 'sandbox' : 'production'
      };

      const subscription = await this.createSubscription(subscriptionRequest);

      return {
        success: true,
        subscription
      };
    } catch (error: unknown) {
      console.error('Platform subscription purchase failed:', error);
      return {
        success: false,
        error: getErrorMessage(error, 'Failed to create subscription')
      };
    }
  }

  /**
   * Get available products from platform billing
   * Returns products with localized prices
   */
  async getPlatformProducts() {
    try {
      const isAvailable = await platformBillingService.isPlatformBillingAvailable();
      if (!isAvailable) {
        return [];
      }

      return await platformBillingService.getProducts();
    } catch (error) {
      console.error('Failed to get platform products:', error);
      return [];
    }
  }

  /**
   * Restore platform purchases
   * Used when user reinstalls app or switches devices
   */
  async restorePlatformPurchases(userId: string): Promise<{
    success: boolean;
    restoredSubscriptions: UserSubscription[];
    error?: string;
  }> {
    try {
      const isAvailable = await platformBillingService.isPlatformBillingAvailable();
      if (!isAvailable) {
        return {
          success: false,
          restoredSubscriptions: [],
          error: 'Platform billing not available'
        };
      }

      // Restore purchases from platform
      const purchases = await platformBillingService.restorePurchases();

      if (purchases.length === 0) {
        return {
          success: true,
          restoredSubscriptions: [],
          error: 'No purchases to restore'
        };
      }

      const restoredSubscriptions: UserSubscription[] = [];

      // Process each restored purchase
      for (const purchase of purchases) {
        if (Platform.OS === 'android' && purchase.purchaseToken) {
          const googlePurchase = googlePlayBillingHandler.processGooglePlayPurchase(purchase);
          if (googlePurchase) {
            const dbData = googlePlayBillingHandler.formatForDatabase(googlePurchase, userId);
            // Check if subscription already exists
            const existing = await this.getUserSubscription(userId);
            if (!existing) {
              const subscriptionRequest: CreateSubscriptionRequest = {
                userId,
                planId: dbData.plan_id,
                billingInterval: dbData.billing_interval,
                paymentProvider: 'google',
                paymentProviderId: googlePurchase.orderId,
                platform: 'android',
                purchaseToken: googlePurchase.purchaseToken,
                environment: dbData.environment as 'sandbox' | 'production'
              };
              const subscription = await this.createSubscription(subscriptionRequest);
              restoredSubscriptions.push(subscription);
            }
          }
        } else if (Platform.OS === 'ios' && purchase.transactionId) {
          const applePurchase = appleStoreBillingHandler.processAppleStorePurchase(purchase);
          if (applePurchase) {
            const dbData = appleStoreBillingHandler.formatForDatabase(applePurchase, userId);
            // Check if subscription already exists
            const existing = await this.getUserSubscription(userId);
            if (!existing) {
              const subscriptionRequest: CreateSubscriptionRequest = {
                userId,
                planId: dbData.plan_id,
                billingInterval: dbData.billing_interval,
                paymentProvider: 'apple',
                paymentProviderId: applePurchase.transactionId,
                platform: 'ios',
                originalTransactionId: applePurchase.originalTransactionId,
                environment: dbData.environment
              };
              const subscription = await this.createSubscription(subscriptionRequest);
              restoredSubscriptions.push(subscription);
            }
          }
        }
      }

      return {
        success: true,
        restoredSubscriptions
      };
    } catch (error: unknown) {
      console.error('Failed to restore purchases:', error);
      return {
        success: false,
        restoredSubscriptions: [],
        error: getErrorMessage(error, 'Failed to restore purchases')
      };
    }
  }

  /**
   * Get subscription management URL for platform
   * Directs users to iOS Settings or Google Play subscriptions
   */
  getPlatformSubscriptionManagementUrl(): string {
    if (Platform.OS === 'ios') {
      return appleStoreBillingHandler.getSubscriptionManagementUrl();
    } else if (Platform.OS === 'android') {
      return googlePlayBillingHandler.getSubscriptionManagementUrl();
    }
    return '';
  }

  /**
   * Validate subscription configuration
   */
  validateConfiguration(): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check if subscription is enabled but payment provider is not configured
    if (ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED) {
      // For web platform, require Stripe configuration
      if (Platform.OS === 'web') {
        if (!ENV_CONFIG.PAYMENT.STRIPE_PUBLIC_KEY) {
          errors.push('Stripe public key is required when subscriptions are enabled');
        }
      }
      // For mobile platforms, platform billing is used
    }

    // Validate subscription plans
    const plans = this.getAvailablePlans();
    if (ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED && plans.length === 0) {
      errors.push('No subscription plans configured');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Start a free trial for a user
   */
  async startFreeTrial(userId: string, planId: string = 'premium'): Promise<UserSubscription> {
    try {
      // Check if user already has or had a subscription/trial
      const { data: existingSubscriptions } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .in('status', ['active', 'trialing', 'canceled']);

      if (existingSubscriptions && existingSubscriptions.length > 0) {
        // Check if user already used their trial
        const hadTrial = existingSubscriptions.some(sub =>
          sub.status === 'trialing' || sub.metadata?.had_trial === true
        );

        if (hadTrial) {
          throw new Error('Free trial has already been used');
        }

        const hasActive = existingSubscriptions.some(sub =>
          sub.status === 'active' || sub.status === 'trialing'
        );

        if (hasActive) {
          throw new Error('User already has an active subscription');
        }
      }

      // Get the subscription plan
      const plan = getSubscriptionPlan(planId);
      if (!plan) {
        throw new Error(`Subscription plan ${planId} not found`);
      }

      // Calculate trial period (7 days)
      const now = new Date();
      const trialEnd = new Date(now);
      trialEnd.setDate(trialEnd.getDate() + 7); // 7-day trial

      // Create subscription with trial status
      const { data: subscription, error } = await supabase
        .from('user_subscriptions')
        .insert({
          user_id: userId,
          plan_id: planId,
          status: 'trialing',
          current_period_start: now.toISOString(),
          current_period_end: trialEnd.toISOString(),
          trial_start: now.toISOString(),
          trial_end: trialEnd.toISOString(),
          cancel_at_period_end: false,
          payment_provider: 'trial',
          payment_provider_subscription_id: `trial_${userId}_${Date.now()}`,
          metadata: {
            trial_type: 'free_7_day',
            had_trial: true,
            source: 'quiz_landing_page',
            plan_name: plan.name,
            plan_tier: plan.tier
          },
          created_at: now.toISOString(),
          updated_at: now.toISOString()
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Sync entitlements to grant premium features
      await entitlementService.syncSubscriptionEntitlements(userId);

      console.log(`Free trial started for user ${userId} with plan ${planId}`);
      return subscription;
    } catch (error) {
      console.error('Failed to start free trial:', error);
      throw error;
    }
  }
}

// Singleton instance
export const subscriptionService = new SubscriptionService();

// Default export
export default subscriptionService;
